/**
 * "Your favourites", drawn from the REVALIDATION read (FR-SEL-021), never from
 * what was true when the star was set.
 *
 * A favourite that changed is shown with what changed. Only SUSPENDED and
 * MODE_UNAVAILABLE stay choosable, as their equivalents in the main list do;
 * a location that closed for good, left the brand, lost its venue, or could
 * not be checked is not offered. Every changed favourite can be removed.
 */

import type { ReactElement } from "react";
import { modeLabel } from "./format.js";
import { LocationRow } from "./LocationRow.js";
import type { Candidate, DirectoryResult, Readiness } from "./types.js";

export interface FavouritesGroupProps {
  /** Stored order, most recently used first. */
  readonly ids: readonly string[];
  /** The `ids=` read, or null while it is in flight. */
  readonly result: DirectoryResult | null;
  readonly mode: string | null;
  /** The main list, only to name a favourite the revalidation did not list. */
  readonly listed: readonly Candidate[];
  readonly readiness: ReadonlyMap<string, Readiness | null>;
  readonly onToggleFavourite: (locationNodeId: string) => void;
  readonly onRemove: (locationNodeId: string) => void;
  readonly onChoose: (candidate: Candidate) => void;
}

const NOT_OFFERED: Readonly<Record<string, string>> = {
  ARCHIVED: "This location has closed permanently.",
  NOT_IN_BRAND: "This location is no longer part of this brand.",
  NO_SITE: "This location is not trading from a venue at the moment.",
};

export function FavouritesGroup({
  ids,
  result,
  mode,
  listed,
  readiness,
  onToggleFavourite,
  onRemove,
  onChoose,
}: FavouritesGroupProps): ReactElement | null {
  if (ids.length === 0) return null;

  return (
    <section aria-labelledby="favourites-heading" className="flex flex-col gap-2">
      <h2 id="favourites-heading" className="text-sm font-medium">
        Your favourites
      </h2>
      {result === null && <p className="text-sm">Checking your favourites...</p>}
      {result !== null && result.kind !== "ok" && (
        <p role="alert" data-testid="favourites-error" className="text-sm">
          We could not check your favourites. Every location is still in the list below.
        </p>
      )}
      {result?.kind === "ok" && (
        <ul data-testid="favourites" className="flex flex-col gap-2">
          {ids.map((id) => {
            const outcome = result.directory.revalidation?.find((r) => r.locationNodeId === id)?.outcome;
            const candidate = result.directory.locations.find((l) => l.locationNodeId === id);
            const testId = `favourite-row-${id}`;
            const common = {
              testId,
              mode: result.directory.mode,
              readiness: readiness.get(id),
              isFavourite: true,
              onToggleFavourite,
              onChoose,
            } as const;

            if (candidate !== undefined && outcome === "OK") {
              return <LocationRow key={id} candidate={candidate} {...common} />;
            }
            if (candidate !== undefined && (outcome === "SUSPENDED" || outcome === "MODE_UNAVAILABLE")) {
              // The row already says it when the directory reports the same fact.
              const note =
                outcome === "SUSPENDED"
                  ? candidate.status === "SUSPENDED" ? null : "Not taking orders at the moment."
                  : candidate.modeAvailable === false ? null : `Does not offer ${modeLabel(mode ?? "")}.`;
              return <LocationRow key={id} candidate={candidate} note={note} onRemove={onRemove} {...common} />;
            }

            const name = (candidate ?? listed.find((l) => l.locationNodeId === id))?.displayName ?? "A saved location";
            return (
              <li
                key={id}
                data-testid={testId}
                className="flex flex-col gap-1 rounded-[var(--border-radius-md)] border border-[var(--color-neutral-300)] p-3 text-sm"
              >
                <h3 className="font-medium">{name}</h3>
                <p role="note">{NOT_OFFERED[outcome ?? ""] ?? "This favourite could not be checked."}</p>
                <button type="button" onClick={() => onRemove(id)} className="self-start underline">
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
